import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideKey, lucideCircleCheck, lucideCircleAlert } from '@ng-icons/lucide';

import { UI } from '../../components/ui';
import { environment } from '../../../environments/environment';

/**
 * Settings page with change password functionality.
 */
@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [FormsModule, NgIcon, ...UI],
  templateUrl: './settings.component.html',
  viewProviders: [
    provideIcons({
      lucideKey,
      lucideCircleCheck,
      lucideCircleAlert,
    }),
  ],
})
export class SettingsComponent {
  private readonly http = inject(HttpClient);

  readonly loading = signal(false);
  readonly success = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';

  onSubmit(): void {
    this.success.set(null);
    this.error.set(null);

    if (!this.currentPassword || !this.newPassword || !this.confirmPassword) {
      this.error.set('All fields are required.');
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.error.set('New passwords do not match.');
      return;
    }

    if (this.newPassword.length < 12) {
      this.error.set('New password must be at least 12 characters.');
      return;
    }

    this.loading.set(true);

    const url = `${environment.apiUrl}/auth/users/set_password/`;
    this.http
      .post(url, {
        current_password: this.currentPassword,
        new_password: this.newPassword,
        new_password_confirm: this.confirmPassword,
      }, { withCredentials: true })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.success.set('Password changed successfully.');
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(
            err?.error?.new_password?.[0] ??
              err?.error?.detail ??
              err?.error?.current_password?.[0] ??
              'Failed to change password.',
          );
        },
      });
  }
}
